import { RefObject, useContext, useEffect } from 'react'
import { UseFormHandleSubmit, UseFormReset } from 'react-hook-form'

import { GetTextParamsHandle } from '@components/TextSettings'

import { ModalType } from '@types'

import { PageContext, SectionType } from '@gantt/components/GanttCreateOrEdit'
import { getIsDuplicateError } from '@gantt/helpers'

type UseAddGanttSegmentDialogProps = {
  reset: UseFormReset<SectionType>
  handleSubmit: UseFormHandleSubmit<SectionType>
  textSettingsRef: RefObject<GetTextParamsHandle>
  onDuplicateError: (error: boolean) => void
  modalType: ModalType
}

export const useAddGanttSegmentDialog = ({
  reset,
  handleSubmit,
  textSettingsRef,
  onDuplicateError,
  modalType,
}: UseAddGanttSegmentDialogProps) => {
  const { onSetCurrentSegment, currentRow, currentSectionsList } = useContext(PageContext)

  useEffect(() => {
    if (currentRow) {
      reset(currentRow)
    }
  }, [currentRow])

  const handleSave = () => {
    handleSubmit(data => {
      const {
        title,
        titles,
        tooltip,
        duration,
        maxDuration,
        datetimeStart,
        datetimeEnd,
        background,
        link,
        labels,
        icons,
        lines,
        activeSegment,
        textColor,
        segmentHeight,
        order,
      } = data

      const textParams = textSettingsRef.current?.getTextParams()

      const newData = {
        id: link.systemName,
        titles,
        title,
        tooltip,
        duration,
        maxDuration,
        datetimeStart,
        datetimeEnd,
        background,
        link,
        labels,
        icons,
        lines,
        activeSegment,
        textColor,
        segmentHeight,
        order,
        textSettings: {
          useTextSettings: data.textSettings?.useTextSettings ?? false,
          fontFamily: textParams?.fontFamily ?? data.textSettings?.fontFamily ?? 'Arial',
          fontSize: String(textParams?.fontSize ?? data.textSettings?.fontSize ?? '14'),
          isBold: textParams ? textParams.isBold === 'true' : data.textSettings?.isBold ?? false,
          isItalic: textParams
            ? textParams.isItalic === 'true'
            : data.textSettings?.isItalic ?? false,
          alignment: textParams?.alignment ?? data.textSettings?.alignment ?? 'left',
          fontColor: textParams?.fontColor ?? data.textSettings?.fontColor ?? '',
        },
      }

      const isDuplicateError = getIsDuplicateError({
        itemsList: currentSectionsList,
        value: newData.id,
        modalType,
      })

      onDuplicateError(isDuplicateError)

      !isDuplicateError && onSetCurrentSegment(newData)
    })()
  }

  return {
    handleSave,
  }
}
